/**
 * Dutch phone number validation and canonicalisation.
 *
 * Mirrors the backend `PhoneNumber` value object so frontend validation and
 * server-side validation cannot drift. Keep this in sync with
 * `src/main/kotlin/nl/scrivio/shared/domain/PhoneNumber.kt`.
 *
 * Accepted input formats:
 * - 10-digit Dutch local number starting with `0` (landline or mobile),
 *   optionally containing whitespace, dashes or parentheses.
 * - International form starting with `+31` (or `0031`) followed by 9 digits,
 *   optionally with a Dutch-convention `(0)` between country code and subscriber.
 * - Empty/null is treated as "not provided" and accepted.
 *
 * Canonical storage forms:
 * - `0NNNNNNNNN` for 10-digit Dutch local numbers.
 * - `+31NNNNNNNNN` for international numbers.
 */
const ALLOWED_INPUT = /^[+\d\s\-()]+$/

/**
 * Returns the canonical form of [raw], or null if [raw] is not a recognised
 * Dutch phone number. Returns null for empty/null input.
 */
export function canonicalizePhoneNumber(raw: string | null | undefined): string | null {
  if (raw == null) return null
  const trimmed = raw.trim()
  if (trimmed === '') return null
  if (!ALLOWED_INPUT.test(trimmed)) return null

  const digits = trimmed.replace(/\D/g, '')

  const isInternational = trimmed.startsWith('+31') || digits.startsWith('0031')
  if (isInternational) {
    let afterCountry: string
    if (digits.startsWith('0031')) afterCountry = digits.slice(4)
    else if (digits.startsWith('31')) afterCountry = digits.slice(2)
    else afterCountry = digits
    const nationalPart = afterCountry.startsWith('0') ? afterCountry.slice(1) : afterCountry
    if (nationalPart.length === 9) {
      return `+31${nationalPart}`
    }
    return null
  }

  if (digits.length === 10 && digits.startsWith('0')) {
    return digits
  }

  return null
}

/**
 * @returns true if [raw] is null/blank, or matches one of the accepted formats.
 */
export function isValidPhoneNumber(raw: string | null | undefined): boolean {
  if (raw == null) return true
  if (raw.trim() === '') return true
  return canonicalizePhoneNumber(raw) !== null
}
